import { Description, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { KIND as ButtonKind } from 'baseui/button';
import {
  Modal,
  ModalBody,
  ModalButton,
  ModalFooter,
  ModalHeader,
} from 'baseui/modal';
import { Spinner } from 'baseui/spinner';
import { Button } from 'components/Button';
import Text from 'components/Text';
import DOMPurify from 'dompurify';
import 'github-markdown-css/github-markdown-light.css';
import mammoth from 'mammoth';
import React, { useState } from 'react';
import { getOpOrdDoc } from 'services/api/inspection/queries';
import { fetcherBlob } from 'services/fetcher';
import { useStitches } from 'theme';
import { isAPIError } from 'utils/error';

const useOpordFile = (inspectionId: string) =>
  useQuery({
    queryKey: ['opord', inspectionId, 'file'],

    queryFn: async () => {
      let link: string | undefined;
      try {
        ({ link } = await getOpOrdDoc({
          params: { inspectionUUID: inspectionId },
        }));
      } catch (error) {
        // A 404 from the endpoint just means no document exists for this
        // inspection — surface it as an empty state, not an error.
        if (isAPIError(error) && error.code === 404) {
          return { hasDocument: false as const };
        }
        throw error;
      }

      if (!link) {
        return { hasDocument: false as const };
      }

      const blob = await fetcherBlob(link);
      const buffer = await blob.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer: buffer });

      return {
        hasDocument: true as const,
        markup: result.value,
        blob,
        link,
      };
    },

    enabled: !!inspectionId,
    staleTime: Infinity
  });

export default function PreviewInspectionDocument({
  inspectionId,
}: {
  inspectionId: string;
}) {
  const { css, theme } = useStitches();
  const [isOpen, setIsOpen] = useState(false);
  const fileQuery = useOpordFile(isOpen ? inspectionId : '');
  function errorMessage() {
    const error = fileQuery.error;
    if (isAPIError(error)) {
      return "We couldn't load the inspection document. Please try again.";
    }
    return "We couldn't open the inspection document. It may be in an unsupported format.";
  }
  if (!inspectionId) {
    return null;
  }

  return (
    <>
      <Description
        onClick={() => setIsOpen(true)}
        className={css({
          backgroundColor: fileQuery.isError ? 'red' : '$primary',
        })}
      />
      <Modal
        onClose={({ closeSource }) =>
          closeSource === 'closeButton' ? setIsOpen(false) : null
        }
        isOpen={isOpen}
        overrides={{
          DialogContainer: {
            style: {
              alignItems: 'center',
            },
          },
          Dialog: {
            style: {
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              height: '80vh',
              width: '80vw',
              marginLeft: theme.sizing.scale600,
              marginRight: theme.sizing.scale600,
              marginBottom: theme.sizing.scale600,
              borderBottomLeftRadius: theme.sizing.scale600,
              borderBottomRightRadius: theme.sizing.scale600,
            },
          },
        }}
      >
        <ModalHeader>Preview Document</ModalHeader>
        <ModalBody
          className={css({
            layout: 'stack',
            flex: '1 1 0',
            overflowY: 'hidden',
          })}
        >
          {fileQuery.isError ? (
            <div className={css({ textAlign: 'center' })}>
              <ErrorIcon
                classes={{
                  root: css({
                    mx: 'auto',
                    height: '48px',
                    width: '48px',
                    color: '$negative500',
                  }),
                }}
              />
              <h3
                className={css({
                  mt: '$scale200',
                  fontWeight: '$normal',
                  color: '$mono500',
                })}
              >
                Error
              </h3>
              <Text
                as="p"
                variant="ParagraphMedium"
                className={css({
                  mt: '$scale100',
                  fontSize: '12px',
                  color: '$mono500',
                })}
              >
                {errorMessage()}
              </Text>
              <div className="mt-6">
                <Button
                  type="button"
                  size="mini"
                  onClick={() => fileQuery.refetch()}
                >
                  Try again.
                </Button>
              </div>
            </div>
          ) : fileQuery.data ? (
            fileQuery.data.hasDocument ? (
              <div
                className={css({
                  layout: 'stack',
                  overflow: 'hidden',
                  spaceY: '$scale600',
                })}
              >
                <a
                  href={fileQuery.data.link}
                  target="_blank"
                  className={css({
                    ':hover': { color: '$primary' },
                  })}
                >
                  <Text>Download File</Text>
                </a>
                <div className={css({ overflowY: 'auto', flex: 1 })}>
                  <div
                    className={`markdown-body `}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(fileQuery.data.markup),
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className={css({ textAlign: 'center' })}>
                <Description
                  classes={{
                    root: css({
                      mx: 'auto',
                      height: '48px',
                      width: '48px',
                      color: '$mono400',
                    }),
                  }}
                />
                <Text
                  as="p"
                  variant="ParagraphMedium"
                  className={css({
                    mt: '$scale200',
                    color: '$mono600',
                  })}
                >
                  No Uploaded Document for this inspection
                </Text>
              </div>
            )
          ) : (
            <div
              className={css({
                display: 'flex',
                justifyContent: 'center',
                p: '$scale600',
                flex: 1,
              })}
            >
              <Spinner />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <ModalButton
            kind={ButtonKind.tertiary}
            onClick={() => setIsOpen(false)}
          >
            Close
          </ModalButton>
        </ModalFooter>
      </Modal>
    </>
  );
}
